import type {
  PortCostDefaultId,
  ScenarioTimeline_ShipFragment,
} from '../../../graphql';
import type { LocationId } from '../../core/Core';
import {
  getDefaultPortCostByType,
  type PortCostEstimateMaps,
} from '../../hooks/usePortCostEstimates';

export type DefaultPortCostEstimates = {
  byName?: {
    id: PortCostDefaultId;
    currency: string;
    cost: number;
  };
  byTypes?: {
    id: PortCostDefaultId;
    currency: string;
    cost: number;
    typeName: string;
  }[];
  byClass?: {
    id: PortCostDefaultId;
    currency: string;
    cost: number;
    typeName: string;
  };
  byId?: { id: number; cost: number };
};

/** Returns the maximum cost and its currency across non-pinned default buckets, or undefined if none exist */
export function getMaxDefaultPortCost(
  defaults: DefaultPortCostEstimates,
): { cost: number; currency: string } | undefined {
  const candidates: { cost: number; currency: string }[] = [];

  if (defaults.byName)
    candidates.push({
      cost: defaults.byName.cost,
      currency: defaults.byName.currency,
    });
  if (defaults.byClass)
    candidates.push({
      cost: defaults.byClass.cost,
      currency: defaults.byClass.currency,
    });
  if (defaults.byTypes) {
    for (const entry of defaults.byTypes) {
      candidates.push({ cost: entry.cost, currency: entry.currency });
    }
  }

  if (candidates.length === 0) return undefined;
  // Compares costs numerically without currency conversion — assumes a single-currency fleet or accepts the approximation
  return candidates.reduce((max, c) => (c.cost > max.cost ? c : max));
}

/** Returns the maximum default cost and its currency across ALL vessels/types at a given location, or undefined if none exist */
export function getMaxDefaultPortCostForLocation(
  locationId: LocationId,
  portCostEstimateMaps: PortCostEstimateMaps,
): { cost: number; currency: string } | undefined {
  const { defaultByVesselId, defaultByTypeId, locationIdsByName } =
    portCostEstimateMaps;
  const candidates: { cost: number; currency: string }[] = [];

  for (const entry of defaultByVesselId.getSubTreeValues(locationId)) {
    candidates.push({ cost: entry.cost, currency: entry.currency });
  }
  for (const entry of defaultByTypeId.getSubTreeValues(locationId)) {
    candidates.push({ cost: entry.cost, currency: entry.currency });
  }

  const wildcardLocationId = locationIdsByName.get('*');
  if (wildcardLocationId && wildcardLocationId !== locationId) {
    for (const entry of defaultByVesselId.getSubTreeValues(
      wildcardLocationId,
    )) {
      candidates.push({ cost: entry.cost, currency: entry.currency });
    }
    for (const entry of defaultByTypeId.getSubTreeValues(wildcardLocationId)) {
      candidates.push({ cost: entry.cost, currency: entry.currency });
    }
  }

  if (candidates.length === 0) return undefined;
  // Compares costs numerically without currency conversion — assumes a single-currency fleet or accepts the approximation
  return candidates.reduce((max, c) => (c.cost > max.cost ? c : max));
}

export function getPortCostDefaults(
  locationId: LocationId,
  portCostDefaultId: PortCostDefaultId | null | undefined,
  portCostEstimateMaps: PortCostEstimateMaps,
  vessel: ScenarioTimeline_ShipFragment,
) {
  const vesselId = vessel.id;
  const typeIds = vessel.vesselAttributesAssociationsByShipId.nodes ?? [];
  const vesselClass = vessel?.vesselAttributeByPrimaryAttributeId;

  const { defaultByTypeId, defaultByVesselId, defaultById, locationIdsByName } =
    portCostEstimateMaps;

  const updatedCost: DefaultPortCostEstimates = {};

  // Check for cost by vessel name
  const defByName = vesselId && defaultByVesselId.get(locationId, vesselId);
  if (defByName) {
    updatedCost.byName = {
      id: defByName.id,
      cost: defByName.cost,
      currency: defByName.currency,
    };
  }

  // Check for costs by vessel types
  for (const type of typeIds) {
    if (!type?.vesselAttributeId) continue;
    const defByType = getDefaultPortCostByType(
      defaultByTypeId,
      locationId,
      type.vesselAttributeId,
      locationIdsByName,
    );

    if (!defByType) continue;
    const existingTypes = updatedCost.byTypes ?? [];
    updatedCost.byTypes = [
      ...existingTypes,
      {
        ...defByType,
      },
    ];
  }

  // Check for cost by vessel class
  if (vesselClass) {
    const defByClass = getDefaultPortCostByType(
      defaultByTypeId,
      locationId,
      vesselClass.id,
      locationIdsByName,
    );
    if (defByClass) {
      updatedCost.byClass = {
        ...defByClass,
      };
    }
  }

  // Check for cost by port cost default ID
  if (portCostDefaultId) {
    const defById = defaultById.get(portCostDefaultId);
    if (defById) {
      updatedCost.byId = { id: portCostDefaultId, cost: defById };
    }
  }

  // applyPrioritiesToPortCosts expects undefined if no defaults were found
  if (Object.keys(updatedCost).length === 0) return undefined;

  return updatedCost;
}
